import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { Flex } from '@mantine/core';
import { Dropzone, DropzoneProps, FileRejection, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { IconPhotoUp } from '@tabler/icons-react';
import { SmartAssistantChatImageAttachmentPreview } from 'components/SmartAssistantComponents/SmartAssistantChatImageAttachmentPreview';
import { useTranslation } from 'react-i18next';
import { NUM_BYTES_IN_MEGABYTE } from 'shared/constants/general-constants';
import { ChatAttachment } from 'shared/interfaces/ChatMessage';

const ACCEPTED_IMAGE_MIME_TYPES = [MIME_TYPES.png, MIME_TYPES.jpeg];
const MAX_CHAT_ATTACHMENT_SIZE_BYTES = 5_000_000;
const MAX_FILE_COUNT = 5;

export interface SmartAssistantChatImageAttachmentDropzoneProps extends Omit<DropzoneProps, 'onDrop'> {
  setImages: (images: ChatAttachment[]) => void;
  dropzoneOpenRef: React.RefObject<() => void>;
  setErrorMessage: (errorMessage: string | null) => void;
  smartAssistantId?: string;
  conversationId?: string;
  enabled?: boolean;
  isGuest?: boolean;
}

export const SmartAssistantChatImageAttachmentDropzone = forwardRef<
  { clear: () => void },
  SmartAssistantChatImageAttachmentDropzoneProps
>(
  (
    {
      setImages,
      dropzoneOpenRef,
      setErrorMessage,
      smartAssistantId,
      conversationId,
      enabled = false,
      isGuest = false,
      ...dropzoneProps
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [imageFiles, setImageFiles] = useState<FileWithPath[]>([]);
    const [imageAttachments, setImageAttachments] = useState<ChatAttachment[]>([]);

    const getImageKey = (file: FileWithPath) => `${file.name}-${file.lastModified}`;

    useEffect(() => {
      setImages(imageAttachments);
    }, [imageAttachments]);

    useImperativeHandle(ref, () => ({
      clear: () => {
        setImageFiles([]);
        setImageAttachments([]);
      },
      onDrop: (files: FileWithPath[]) => {
        onDrop(files);
      }
    }));

    const handleUpload = (image: ChatAttachment) => {
      setImageAttachments((prev) => [...prev, image]);
    };

    const handleRemove = (imageFile: FileWithPath, image?: ChatAttachment) => {
      if (image) setImageAttachments((prev) => prev.filter((a) => a._id !== image._id));
      setImageFiles((prev) => prev.filter((f) => getImageKey(f) !== getImageKey(imageFile)));
    };

    const onDrop = (files: FileWithPath[]) => {
      const allFiles = [...imageFiles, ...files];
      if (allFiles.length > MAX_FILE_COUNT) {
        setErrorMessage(t('FEED_BACK.SMART_ASSISTANT_INPUT.FAILURE.IMAGES_MAX_FILE_COUNT', { max: MAX_FILE_COUNT }));
        return;
      }
      const totalSize = allFiles.reduce((acc, curr) => acc + curr.size, 0);
      if (totalSize > MAX_CHAT_ATTACHMENT_SIZE_BYTES) {
        setErrorMessage(
          t('FEED_BACK.SMART_ASSISTANT_INPUT.FAILURE.IMAGES_MAX_FILE_SIZE', {
            max: MAX_CHAT_ATTACHMENT_SIZE_BYTES / NUM_BYTES_IN_MEGABYTE
          })
        );
        return;
      }
      setImageFiles((prev) => [...prev, ...files]);
    };

    const handleReject = (fileRejections: FileRejection[]) => {
      if (fileRejections.length > MAX_FILE_COUNT) {
        setErrorMessage(t('FEED_BACK.SMART_ASSISTANT_INPUT.FAILURE.IMAGES_MAX_FILE_COUNT', { max: MAX_FILE_COUNT }));
      }

      const totalSize = fileRejections.reduce((acc, curr) => acc + curr.file.size, 0);

      if (totalSize > MAX_CHAT_ATTACHMENT_SIZE_BYTES) {
        setErrorMessage(
          t('FEED_BACK.SMART_ASSISTANT_INPUT.FAILURE.IMAGES_MAX_FILE_SIZE', {
            max: MAX_CHAT_ATTACHMENT_SIZE_BYTES / NUM_BYTES_IN_MEGABYTE
          })
        );
      }
    };

    return enabled ? (
      <Dropzone
        p="0"
        openRef={dropzoneOpenRef}
        onDrop={onDrop}
        accept={ACCEPTED_IMAGE_MIME_TYPES}
        maxSize={MAX_CHAT_ATTACHMENT_SIZE_BYTES}
        maxFiles={MAX_FILE_COUNT}
        multiple
        onReject={handleReject}
        activateOnClick={false}
        styles={{
          root: {
            border: 'none',
            backgroundColor: 'transparent !important',
            '&:hover': { cursor: 'default' }
          },
          inner: {
            pointerEvents: 'all',
            position: 'relative',
            backgroundColor: 'transparent !important'
          }
        }}>
        <Dropzone.Accept>
          <Flex
            align="center"
            justify="center"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 2,
              backdropFilter: 'blur(1px)'
            }}>
            <IconPhotoUp style={{ height: 40, width: 40 }} />
          </Flex>
        </Dropzone.Accept>

        {smartAssistantId && conversationId && imageFiles.length > 0 && (
          <Flex direction="row" align="center" gap="xs" w="100%" my={5}>
            {imageFiles.map((file) => {
              return (
                <SmartAssistantChatImageAttachmentPreview
                  key={getImageKey(file)}
                  imageFile={file}
                  smartAssistantId={smartAssistantId}
                  conversationId={conversationId}
                  onRemove={handleRemove}
                  onUpload={handleUpload}
                  setErrorMessage={setErrorMessage}
                  isGuest={isGuest}
                />
              );
            })}
          </Flex>
        )}

        {dropzoneProps.children}
      </Dropzone>
    ) : (
      <>{dropzoneProps.children}</>
    );
  }
);

SmartAssistantChatImageAttachmentDropzone.displayName = 'SmartAssistantChatImageAttachmentDropzone';
